import createSagaMiddleware from 'redux-saga';
import { Vermittlernummer } from '@ps20/bff-models';
import { App } from '@ps20-frontend/frontend-models';
import {
  GlobalPut,
  integrateReduxDevtoolsExtension,
  NgRefreshStore,
} from '@ps20-frontend/shop-integration-redux';
import { configureStore, Dispatch, Middleware, Tuple, UnknownAction } from '@reduxjs/toolkit';

import combineSectionReducers from 'combine-section-reducers';
import { rootSaga } from './sagas/rootSaga';
import { amisDataReceivedReducer } from './amisDataReceived.reducer';
import { TransientState, transientStateReducer } from './transientState.reducer';
import { canNavigateReducer } from './canNavigate.reducer';
import {
  INITIAL_STATE_SAVE_PROTOCOL,
  SaveProtocol,
  saveProtocolReducer,
} from './saveProtocol.reducer';
import { rootReducer } from './root.reducer';
import { accountManagerReducer } from './accountManager.reducer';
import { azamVorgangsIdReducer } from './azamVorgangsId.reducer';

export interface IAppState {
  version: number;
  canNavigate: boolean;
  azamVorgangsId?: string;
  amisDataReceived: boolean;
  accountManager?: Vermittlernummer;
  saveProtocol: SaveProtocol;
  transientState: TransientState;
}

export const INITIAL_STATE: IAppState = {
  version: 1,
  canNavigate: null,
  azamVorgangsId: null,
  amisDataReceived: false,
  saveProtocol: INITIAL_STATE_SAVE_PROTOCOL,
  accountManager: null,
  transientState: {
    role: null,
    accessToken: null,
    datenhaltung: {
      kdv: {
        sessionId: null,
        perId: null,
        vorId: null,
        copyFlag: 0,
        amisupd: null,
      },
      av: {
        cryptId: null,
        angebotsBuendelId: null,
      },
      sdw: {
        partyId: null,
        role: null,
        code: null,
        state: null,
        accessToken: null,
      },
      auth: {
        authenticated: null,
      },
      permasession: null,
      azamVorgangsId: null,
    },
    responseCache: {
      items: [],
    },
    avLoaded: false,
    appIsInitialized: false,
    appSourcePathConfigs: {},
    appVorbelegungPending: {},
    errorPageOpen: false,
    errorPageTextIds: null,
    sessionExpired: false,
    userLoggedOut: false,
    constructionPageOpen: false,
    forwardedToUnderwriterPageOpen: false,
    loadVorvertraege: {
      loadedOrSkipped: false,
      kundennummerReceived: false,
      kundennummer: null,
      vermittlernummer: null,
    },
    featureToggles: null,
    kooperationen: null,
    modal: {
      modalOpen: false,
      textContent: null,
      styleObject: null,
    },
    vnAddressInvalid: {
      open: false,
      einstiegResponse: null,
      invalidAddressConfirmed: false,
    },
    settings: null,
  },
};

export const reducers = combineSectionReducers({
  version: (version: number = null) => version,
  transientState: transientStateReducer,
  amisDataReceived: amisDataReceivedReducer,
  accountManager: accountManagerReducer,
  canNavigate: canNavigateReducer,
  saveProtocol: saveProtocolReducer,
  azamVorgangsId: azamVorgangsIdReducer,
});

export function storeFactory(initialState: IAppState): NgRefreshStore<IAppState> {
  const ngRefreshStore = new NgRefreshStore<IAppState>(App.SHOP);
  const sagaMiddleware = createSagaMiddleware();
  const store = configureStore({
    preloadedState: initialState,
    devTools: integrateReduxDevtoolsExtension('shop'),
    reducer: rootReducer(reducers),
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware, ngRefreshStore.ngRefreshMiddleware) as unknown as Tuple<ReadonlyArray<Middleware<Record<string, never>, IAppState, Dispatch<UnknownAction>>>>,
  });
  sagaMiddleware.run(rootSaga);
  ngRefreshStore.setDelegate(store);

  return ngRefreshStore;
}

export const localStore: NgRefreshStore<IAppState> = storeFactory(INITIAL_STATE);

GlobalPut.getInstance().globalEventDispatcherFunction = () => {
  return localStore.globalEventDispatcherRef;
};
